import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { CardContent, Link } from '@mui/material';
import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { ReviewSummaryDialog } from 'apps/wrapper/src/components/shared/patient-feedback/dialogs/review-summary/review-summary';
import { SurveyResultsDialog } from 'apps/wrapper/src/components/shared/patient-feedback/dialogs/survey-results/survey-results';
import PatientFeedbackService from 'apps/wrapper/src/services/patient-feedback-service/patient-feedback-service';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { useCreateReviewSurvey, useIsReviewSurveyEnabled } from '../../../../../../hooks/use-review-survey';
import {
  FeedbackType,
  PatientFeedback
} from '../../../../../../services/patient-feedback-service/patient-feedback-service-types';
import {
  DotSeparatedTitle,
  ListItem
} from '../../../../../layout/slideout/patient-profile-widget/patient-profile-widget.styles';
import { NotesSkeleton } from '../../../../../layout/slideout/patient-profile-widget/views/notes/notes.skeleton';
import {
  FeedbackInfo,
  FeedbackText
} from '../../../../../layout/slideout/patient-profile-widget/views/patient-feedback/patient-feedback.styles';
import { NoDataComponent } from '../../../../../shared/grid/no-data';
import { StarRating } from '../../../../../shared/star-rating/star-rating';
import { usePatientProfilePage } from '../../hooks';
import { PatientProfileRoutePath } from '../../navigation/route-config';
import { Card, CardTitle } from '../../patient-profile-page.styles';
import { feedbackTypeToTitle } from '../feedback/feedback';
import { SurveyRating } from '../feedback/feedback.styles';
import { ButtonsRight } from './overview.styles';

export const PatientFeedbackView = () => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { showError } = useNotification();
  const { goTo } = usePatientProfilePage();
  const { contactDetails, contactLocation, patientInfo, loggedInUserLocation, locationId } = usePatientProfile();
  const [patientFeedbacks, setPatientFeeedbacks] = useState<PatientFeedback[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingReview, setIsLoadingReview] = useState(false);
  const [isLoadingSurvey, setIsLoadingSurvey] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(null);

  const patientFeedbackService = useMemo(
    () => PatientFeedbackService.getInstance(config, oidcService),
    [config, oidcService]
  );

  const isReviewSurveyEnabled = useIsReviewSurveyEnabled({
    contactDetails,
    contactLocation,
    patientInfo,
    loggedInUserLocation
  });

  const loadPatientFeedback = useCallback(async () => {
    if (!contactDetails) return;

    setIsLoading(true);

    try {
      const feedback = await patientFeedbackService.getPatientFeedback(contactDetails.id, locationId);

      setPatientFeeedbacks(feedback.slice(0, 2));
    } catch {
      showError('Unable to load patient feedback.');
    } finally {
      setIsLoading(false);
    }
  }, [contactDetails?.id, locationId]);

  useEffect(() => {
    loadPatientFeedback();
  }, [loadPatientFeedback]);

  const { request } = useCreateReviewSurvey({ contactDetails, locationId });

  const handleSendSurveyClick = async () => {
    setIsLoadingSurvey(true);
    await request(FeedbackType.Survey);
    setIsLoadingSurvey(false);
  };

  const handleSendReviewClick = async () => {
    setIsLoadingReview(true);
    await request(FeedbackType.Review);
    setIsLoadingReview(false);
  };

  const baseUrl = useMemo(
    () =>
      `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? `:${window.location.port}` : ''
      }`,
    [window.location]
  );

  const handleFeedbackItemClick = (feedback: PatientFeedback) => {
    if (feedback.feedbackType === FeedbackType.Review) {
      setSelectedReviewId(feedback.idPatientFeedback);
    } else {
      setSelectedSurveyId(feedback.idPatientFeedback);
    }
  };

  return (
    <>
      <Card>
        <CardContent>
          <CardTitle className="hr clickable" onClick={() => goTo(PatientProfileRoutePath.FEEDBACK)}>
            Feedback
            <ArrowForwardIosIcon />
          </CardTitle>
          {isLoading && <NotesSkeleton itemCount={1} />}
          {!isLoading && !patientFeedbacks.length && <NoDataComponent />}
          {!isLoading &&
            !!patientFeedbacks.length &&
            patientFeedbacks.map((patientFeedback) => (
              <ListItem className="feedback" onClick={() => handleFeedbackItemClick(patientFeedback)}>
                <FeedbackInfo className="feedback-info">
                  <DotSeparatedTitle
                    leftTitle={patientFeedback.date ? moment(patientFeedback.date).format('MM/DD/YYYY') : null}
                    rightTitle={feedbackTypeToTitle[patientFeedback.feedbackType]}
                  />
                  {patientFeedback.feedbackType === FeedbackType.Review && (
                    <StarRating rating={patientFeedback.ratingScore} />
                  )}
                  {patientFeedback.feedbackType === FeedbackType.Survey && (
                    <SurveyRating score={patientFeedback.ratingScore}>{patientFeedback.ratingScore}%</SurveyRating>
                  )}
                </FeedbackInfo>
                <FeedbackText>{patientFeedback.message}</FeedbackText>
              </ListItem>
            ))}
          <ButtonsRight>
            <Link
              disabled={!isReviewSurveyEnabled || isLoading || isLoadingReview || isLoadingSurvey}
              onClick={handleSendReviewClick}
              component="button"
              variant="body2"
            >
              Send Review
            </Link>
            <Link
              disabled={!isReviewSurveyEnabled || isLoading || isLoadingReview || isLoadingSurvey}
              onClick={handleSendSurveyClick}
              component="button"
              variant="body2"
            >
              Send Survey
            </Link>
          </ButtonsRight>
        </CardContent>
      </Card>
      {selectedReviewId && (
        <ReviewSummaryDialog
          reviewId={selectedReviewId}
          open={selectedReviewId != null}
          onClose={() => setSelectedReviewId(null)}
        />
      )}
      {selectedSurveyId && (
        <SurveyResultsDialog
          surveyId={selectedSurveyId}
          open={selectedSurveyId != null}
          onClose={() => setSelectedSurveyId(null)}
        />
      )}
    </>
  );
};

export default PatientFeedbackView;
